/**
 * CTSK-355 — page-header `?` help link.
 *
 * Mirrors the CTSK-281/289 help-link affordance as a compact circular
 * `?` icon for page headers (right side). Links to `/help/<slug>` when
 * the page has a dedicated help doc in the HELP_PAGES catalog
 * (CTSK-356), or to the `/help` index when it doesn't — the icon
 * always renders, so every swept surface gets a help affordance even
 * before its doc ships (missing docs are tracked for follow-up
 * filing, see the CTSK-355 ship report).
 *
 * Navigation is in-app (SPA) when the host passes onNavigate; the
 * href keeps middle-click / open-in-new-tab working either way.
 */
export function HelpLink({
  pageName,
  slug = null,
  onNavigate,
}: {
  /** Human page name, rendered into the tooltip ("Open help for X"). */
  pageName: string;
  /** HELP_PAGES slug; null/absent → the /help index. */
  slug?: string | null;
  onNavigate?: (path: string) => void;
}) {
  const target = slug ? `/help/${slug}` : '/help';
  return (
    <a
      href={target}
      aria-label={`Open help for ${pageName}`}
      title={`Open help for ${pageName}`}
      onClick={(e) => {
        if (onNavigate) {
          e.preventDefault();
          onNavigate(target);
        }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: '1px solid var(--border-strong)',
        color: 'var(--text-muted)',
        background: 'transparent',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
        textDecoration: 'none',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = 'var(--accent-primary)';
        e.currentTarget.style.borderColor = 'var(--accent-primary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--text-muted)';
        e.currentTarget.style.borderColor = 'var(--border-strong)';
      }}
    >
      ?
    </a>
  );
}
